<script lang="ts">
  import { ApiError, claimDevice } from '$lib/api';
  import { deviceHref } from '$lib/nav';
  import { session } from '$lib/session.svelte';
  import Button from '$lib/components/Button.svelte';
  import Card from '$lib/components/Card.svelte';

  let puk = $state('');
  let name = $state('');
  let error = $state<string | null>(null);
  let submitting = $state(false);

  const role = $derived(session.user?.role);
  /** Staff ops role (`user`) registers hardware; clients claim. Admin may claim for smoke tests. */
  const isStaffUser = $derived(role === 'user');

  async function onSubmit(e: Event) {
    e.preventDefault();
    error = null;
    if (isStaffUser) {
      error = 'Staff register devices under Register device. Only clients claim with a PUK.';
      return;
    }
    const pukNorm = puk.trim().toUpperCase();
    if (!/^[0-9A-F]{16}$/.test(pukNorm)) {
      error = 'PUK must be 16 hexadecimal characters.';
      return;
    }

    submitting = true;
    try {
      const res = await claimDevice({
        puk: pukNorm,
        name: name.trim() || undefined,
      });
      window.location.hash = deviceHref(res.device.id);
    } catch (err) {
      error = err instanceof ApiError ? err.message : 'Claim failed. Try again.';
    } finally {
      submitting = false;
    }
  }
</script>

<div class="page">
  <header class="head">
    <div>
      <a class="back" href="#/devices">← Devices</a>
      <h2>Claim device</h2>
      <p class="sub">
        Enter the PUK from the device label (or from your installer). The system already knows the
        UID — on success the unit is bound to your account and the PUK is consumed.
      </p>
    </div>
  </header>

  {#if isStaffUser}
    <Card title="Clients claim devices">
      <p class="muted">
        Staff add hardware with <strong>Register device</strong>. Clients use this page with the PUK
        only.
      </p>
      <div class="actions">
        <a class="link-btn" href="#/devices/register">Go to Register device</a>
        <a class="cancel" href="#/devices">Cancel</a>
      </div>
    </Card>
  {:else}
    <Card title="Claim with PUK" subtitle="PUK is 16 hex · UID is not required">
      <form class="form" autocomplete="off" onsubmit={(e) => void onSubmit(e)}>
        <label class="field">
          <span class="label">PUK</span>
          <input
            class="input mono"
            id="claim-device-puk"
            name="ucc_claim_puk"
            type="text"
            inputmode="text"
            autocomplete="off"
            autocapitalize="characters"
            spellcheck="false"
            maxlength={16}
            placeholder="16-character hex PUK"
            bind:value={puk}
            disabled={submitting}
            required
          />
        </label>

        <label class="field">
          <span class="label">Display name <span class="opt">(optional)</span></span>
          <input
            class="input"
            id="claim-device-name"
            name="ucc_claim_name"
            type="text"
            autocomplete="off"
            placeholder="e.g. Line A pump"
            bind:value={name}
            disabled={submitting}
          />
        </label>

        {#if error}
          <p class="err" role="alert">{error}</p>
        {/if}

        <div class="actions">
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Claiming…' : 'Claim device'}
          </Button>
          <a class="cancel" href="#/devices">Cancel</a>
        </div>
      </form>
    </Card>
  {/if}
</div>

<style>
  .page {
    display: flex;
    flex-direction: column;
    gap: var(--space-5, 1.25rem);
    max-width: 32rem;
  }
  .head {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
  }
  .back {
    font-size: 0.875rem;
    color: var(--text-muted);
    text-decoration: none;
  }
  .back:hover {
    color: var(--text);
  }
  h2 {
    margin: 0.25rem 0 0;
    font-size: 1.5rem;
  }
  .sub {
    margin: 0.35rem 0 0;
    color: var(--text-muted);
    font-size: 0.9375rem;
    line-height: 1.45;
  }
  .form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
  .field {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
  }
  .label {
    font-size: 0.8125rem;
    font-weight: 600;
    color: var(--text-muted);
  }
  .opt {
    font-weight: 400;
    opacity: 0.8;
  }
  .input {
    border: 1px solid var(--border);
    border-radius: 8px;
    padding: 0.55rem 0.75rem;
    font: inherit;
    background: var(--surface);
    color: var(--text);
  }
  .input.mono {
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }
  .input:focus {
    outline: 2px solid color-mix(in srgb, var(--accent, #2563eb) 55%, transparent);
    outline-offset: 1px;
  }
  .input:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
  .err {
    margin: 0;
    color: var(--danger, #ef4444);
    font-size: 0.875rem;
  }
  .actions {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-top: 0.25rem;
    flex-wrap: wrap;
  }
  .cancel {
    font-size: 0.875rem;
    color: var(--text-muted);
  }
  .muted {
    color: var(--text-muted);
    line-height: 1.45;
  }
  .link-btn {
    display: inline-flex;
    align-items: center;
    padding: 0.5rem 0.9rem;
    border-radius: 8px;
    background: var(--accent, #2563eb);
    color: #fff;
    text-decoration: none;
    font-size: 0.875rem;
    font-weight: 600;
  }
</style>
